import { FC, useCallback, useEffect, useRef, useState } from 'react';
import type { MermaidConfig } from 'mermaid';
import { colors } from '@/theme/colors';

const MERMAID_TIMEOUT_MS = 10000;

type MermaidThemeMode = 'light' | 'dark';

const LIGHT_THEME_VARIABLES: MermaidConfig['themeVariables'] = {
  fontFamily: 'inherit',
  ...colors.mermaid.light,
};

const DARK_THEME_VARIABLES: MermaidConfig['themeVariables'] = {
  fontFamily: 'inherit',
  ...colors.mermaid.dark,
};

const BASE_MERMAID_CONFIG: MermaidConfig = {
  startOnLoad: false,
  theme: 'neutral',
  securityLevel: 'loose', // 'strict' breaks some diagrams, sanitization is built-in
  fontFamily: 'inherit',
  logLevel: 'error',
  suppressErrorRendering: true,
  deterministicIds: true,
  deterministicIDSeed: 'markdown-mermaid',
  flowchart: {
    useMaxWidth: false,
    nodeSpacing: 48,
    rankSpacing: 48,
    htmlLabels: true,
  },
  class: {
    useMaxWidth: false,
  },
  er: {
    useMaxWidth: false,
    nodeSpacing: 120,
    rankSpacing: 90,
  },
  sequence: {
    useMaxWidth: false,
  },
  mindmap: {
    useMaxWidth: false,
  },
  c4: {
    useMaxWidth: false,
  },
  gantt: {
    useMaxWidth: false,
    topPadding: 48,
    barHeight: 28,
    barGap: 10,
  },
  journey: {
    useMaxWidth: false,
    leftMargin: 56,
    taskMargin: 24,
  },
  timeline: {
    useMaxWidth: false,
    leftMargin: 56,
    taskMargin: 24,
  },
  state: {
    useMaxWidth: false,
  },
  pie: {
    useMaxWidth: false,
  },
  requirement: {
    useMaxWidth: false,
  },
  gitGraph: {
    useMaxWidth: false,
  },
  quadrantChart: {
    useMaxWidth: false,
  },
  xyChart: {
    useMaxWidth: false,
  },
  sankey: {
    useMaxWidth: false,
  },
  architecture: {
    useMaxWidth: false,
  },
  block: {
    useMaxWidth: false,
  },
  radar: {
    useMaxWidth: false,
  },
  kanban: {
    useMaxWidth: false,
  },
  packet: {
    useMaxWidth: false,
  },
};

const buildMermaidConfig = (mode: MermaidThemeMode): MermaidConfig => ({
  ...BASE_MERMAID_CONFIG,
  theme: mode === 'dark' ? 'dark' : 'neutral',
  themeVariables: mode === 'dark' ? DARK_THEME_VARIABLES : LIGHT_THEME_VARIABLES,
});

const resolveThemeMode = (): MermaidThemeMode => {
  if (typeof document === 'undefined') {
    return 'light';
  }

  const bodyHasDark = document.body?.classList?.contains('dark');
  const rootHasDark = document.documentElement?.classList?.contains('dark');
  return bodyHasDark || rootHasDark ? 'dark' : 'light';
};

const DEFAULT_ERROR_MESSAGE = 'Failed to render diagram';

type MermaidApi = typeof import('mermaid').default;

let mermaidPromise: Promise<MermaidApi> | null = null;
let mermaidConfiguredTheme: MermaidThemeMode | null = null;

const loadMermaid = async (mode: MermaidThemeMode): Promise<MermaidApi> => {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then(({ default: mermaid }) => mermaid);
  }

  const mermaid = await mermaidPromise;

  if (mermaidConfiguredTheme !== mode) {
    mermaid.initialize(buildMermaidConfig(mode));
    mermaidConfiguredTheme = mode;
  }

  return mermaid;
};

const normalizeMermaidError = (error: unknown): string => {
  if (!error) return DEFAULT_ERROR_MESSAGE;

  if (error instanceof Error) {
    const rawMessage = error.message || DEFAULT_ERROR_MESSAGE;
    const parseMatch = rawMessage.match(/Parse error on line (\d+):(.*)/i);

    if (parseMatch) {
      const [, line, detail] = parseMatch;
      return `Syntax error on line ${line}: ${detail.trim()}`;
    }

    return rawMessage;
  }

  if (typeof error === 'string') {
    return error;
  }

  return DEFAULT_ERROR_MESSAGE;
};

interface MermaidDiagramProps {
  chart: string;
  id?: string;
}

/**
 * MermaidDiagram - Production-ready component for rendering Mermaid diagrams
 *
 * Features:
 * - Lazy loading of mermaid library for optimal bundle size
 * - Error handling with graceful fallback
 * - Automatic re-rendering on content changes
 * - Unique ID generation to avoid conflicts
 * - Security: Content sanitization handled by mermaid.js internally
 */
export const MermaidDiagram: FC<MermaidDiagramProps> = ({ chart, id }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [themeMode, setThemeMode] = useState<MermaidThemeMode>(() => resolveThemeMode());

  // Generate unique ID if not provided
  const diagramId = useRef(id || `mermaid-${Math.random().toString(36).substr(2, 9)}`);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const updateTheme = () => {
      setThemeMode(resolveThemeMode());
    };

    updateTheme();

    const target = document.body ?? document.documentElement;
    if (!target) {
      return;
    }

    const observer = new MutationObserver(updateTheme);
    observer.observe(target, { attributes: true, attributeFilter: ['class'] });

    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'theme') {
        updateTheme();
      }
    };

    window.addEventListener('storage', handleStorage);

    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    const handleMedia = () => updateTheme();
    mediaQuery?.addEventListener('change', handleMedia);

    return () => {
      observer.disconnect();
      window.removeEventListener('storage', handleStorage);
      mediaQuery?.removeEventListener('change', handleMedia);
    };
  }, []);

  const applyResponsiveSizing = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const svgElement = container.querySelector<SVGSVGElement>('svg');
    if (!svgElement) return;

    const ensureViewBox = () => {
      if (svgElement.hasAttribute('viewBox')) return;

      const widthAttr = svgElement.getAttribute('width');
      const heightAttr = svgElement.getAttribute('height');

      const widthValue = widthAttr ? parseFloat(widthAttr) : Number.NaN;
      const heightValue = heightAttr ? parseFloat(heightAttr) : Number.NaN;

      if (
        !Number.isNaN(widthValue) &&
        !Number.isNaN(heightValue) &&
        widthValue > 0 &&
        heightValue > 0
      ) {
        svgElement.setAttribute('viewBox', `0 0 ${widthValue} ${heightValue}`);
      }
    };

    const resolveNaturalWidth = () => {
      const viewBox = svgElement.viewBox?.baseVal;
      if (viewBox && viewBox.width > 0) {
        return viewBox.width;
      }

      const widthAttr = svgElement.getAttribute('width');
      if (widthAttr && !widthAttr.endsWith('%')) {
        const parsed = parseFloat(widthAttr);
        if (!Number.isNaN(parsed) && parsed > 0) {
          return parsed;
        }
      }

      try {
        const bbox = svgElement.getBBox?.();
        if (bbox && bbox.width > 0) {
          return bbox.width;
        }
      } catch (bboxError) {
        console.warn('Unable to compute SVG bounding box:', bboxError);
      }

      const bounds = svgElement.getBoundingClientRect();
      return bounds.width > 0 ? bounds.width : undefined;
    };

    ensureViewBox();

    const parentWidth = container.parentElement?.clientWidth ?? container.clientWidth;
    const naturalWidth = resolveNaturalWidth();

    if (parentWidth && naturalWidth) {
      if (naturalWidth <= parentWidth) {
        svgElement.style.width = '100%';
        svgElement.style.maxWidth = '100%';
      } else {
        svgElement.style.width = `${naturalWidth}px`;
        svgElement.style.maxWidth = 'none';
      }
    } else {
      svgElement.style.width = '100%';
      svgElement.style.maxWidth = '100%';
    }

    svgElement.style.height = 'auto';
    svgElement.style.display = 'block';
    svgElement.style.overflow = 'visible';
    svgElement.removeAttribute('height');

    if (!svgElement.hasAttribute('preserveAspectRatio')) {
      svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    }

    if (!svgElement.hasAttribute('role')) {
      svgElement.setAttribute('role', 'img');
    }
  }, []);

  useEffect(() => {
    let isActive = true;
    let timeoutHandle: number | null = null;

    const renderDiagram = async () => {
      const container = containerRef.current;
      if (!container) {
        setIsLoading(false);
        return;
      }

      const trimmedChart = chart.trim();
      if (!trimmedChart) {
        throw new Error('Diagram content is empty');
      }

      container.innerHTML = '';

      const mermaid = await loadMermaid(themeMode);

      const renderPromise = mermaid.render(diagramId.current, trimmedChart);
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutHandle = window.setTimeout(
          () => reject(new Error('Diagram rendering timeout')),
          MERMAID_TIMEOUT_MS
        );
      });

      try {
        const { svg } = await Promise.race([renderPromise, timeoutPromise]);

        if (!isActive || !containerRef.current) return;

        containerRef.current.innerHTML = svg;
        requestAnimationFrame(() => {
          if (!isActive) return;
          applyResponsiveSizing();
        });
        setIsLoading(false);
      } finally {
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
          timeoutHandle = null;
        }
      }
    };

    setIsLoading(true);
    setError(null);

    renderDiagram().catch(err => {
      if (!isActive) return;

      console.error('Mermaid rendering error:', err);
      setError(normalizeMermaidError(err));
      setIsLoading(false);
    });

    return () => {
      isActive = false;

      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
      }
    };
  }, [chart, themeMode, applyResponsiveSizing]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      applyResponsiveSizing();
    });

    resizeObserver.observe(container.parentElement ?? container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [applyResponsiveSizing]);

  if (error) {
    return (
      <div className="border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 rounded-lg p-4 my-4">
        <div className="flex items-start gap-2 mb-2">
          <span className="text-red-600 dark:text-red-400 text-lg">⚠️</span>
          <div className="flex-1">
            <p className="text-sm text-red-800 dark:text-red-200 font-semibold">
              Failed to render diagram
            </p>
            <pre className="text-xs text-red-600 dark:text-red-400 overflow-auto mt-1 whitespace-pre-wrap">
              {error}
            </pre>
          </div>
        </div>
        <details className="mt-3">
          <summary className="text-xs text-red-700 dark:text-red-300 cursor-pointer hover:underline font-medium">
            Show diagram code
          </summary>
          <pre className="mt-2 text-xs text-red-600 dark:text-red-400 overflow-auto bg-red-100 dark:bg-red-900/30 p-3 rounded whitespace-pre-wrap font-mono border border-red-200 dark:border-red-800">
            {chart}
          </pre>
        </details>
      </div>
    );
  }

  return (
    <div className="my-4 overflow-x-auto">
      <div className="mermaid-container relative flex w-full justify-center">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-grey-medium dark:text-grey-medium-dark animate-pulse">
            Loading diagram...
          </div>
        )}
        <div
          ref={containerRef}
          className={`mermaid-diagram w-full ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          style={{ minWidth: 'min(100%, 18rem)' }}
        />
      </div>
    </div>
  );
};

export default MermaidDiagram;
